'use client';

import type { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { Mail, Phone } from 'lucide-react';
import { Button } from '@zillow/constellation-10';
import { LucideIcon } from '@/src/components/LucideIcon';
import { compactPhoneAction, contactActions, fullPhoneAction, messageLabelStyles } from './listingCard.styles';

type ListingCardContactActionsProps = {
  contactPhone?: string | null;
  messageDisabled: boolean;
  onCall: () => void;
  onMessage: () => void;
};

function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

function formatPhoneNumber(phone: string): string {
  const digits = phoneDigits(phone);
  const localDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (localDigits.length !== 10) return phone;

  return `(${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
}

export function toPhoneHref(phone: string): string | null {
  const digits = phoneDigits(phone);
  return digits ? `tel:${digits}` : null;
}

export function ListingCardContactActions({
  contactPhone,
  messageDisabled,
  onCall,
  onMessage,
}: ListingCardContactActionsProps) {
  const t = useTranslations('hdp');
  const tContact = useTranslations('contactForm');
  const phoneNumber = contactPhone ?? '';
  const phoneHref = toPhoneHref(phoneNumber);

  const handleMessage = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onMessage();
  };

  const handleCall = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    onCall();
  };

  return (
    <div className={contactActions} role="group" aria-label={tContact('titleContact')}>
      <Button.Root
        data-testid="button-message"
        type="button"
        tone="brand"
        emphasis="filled"
        size="sm"
        fluid
        onClick={handleMessage}
        disabled={messageDisabled}
        aria-label={t('messageCta')}
        aria-busy={messageDisabled}
      >
        <Button.Icon>
          <LucideIcon icon={Mail} size="sm" />
        </Button.Icon>
        <Button.Label css={messageLabelStyles}>{t('messageCta')}</Button.Label>
      </Button.Root>
      {phoneHref && (
        <>
          <span className={compactPhoneAction}>
            <Button
              asChild
              tone="brand"
              emphasis="outlined"
              size="sm"
              fluid
              icon={<LucideIcon icon={Phone} size="sm" />}
            >
              <a href={phoneHref} onClick={handleCall} data-testid="link-call">
                {t('callCta')}
              </a>
            </Button>
          </span>
          <span className={fullPhoneAction}>
            <Button asChild tone="brand" emphasis="bare" size="md" fluid>
              <a
                href={phoneHref}
                onClick={handleCall}
                aria-label={`${t('callCta')} ${formatPhoneNumber(phoneNumber)}`}
                data-testid="link-call-phone-number"
              >
                {formatPhoneNumber(phoneNumber)}
              </a>
            </Button>
          </span>
        </>
      )}
    </div>
  );
}
